import type { FC, ReactNode } from 'react'

import { useRouter } from 'next/router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeftRight, ChevronLeft, ChevronRight } from 'lucide-react'
import { Reveal } from '@/components/generals/reveal'
import { AppIcon } from '@/components/generals/app-icon'
import { useContact } from '@/components/generals/contact-dialog'
import { AppSelect } from '@/components/landing/app-select'
import { defaultPartner, shortName, workflowsFor } from '@/lib/workflows'
import { showcaseApps, showcaseQueryAliases } from '@/constants/full-service'

const PER_PAGE = 9

export const Showcase: FC = (): ReactNode => {
    const router = useRouter()
    const { openContact } = useContact()
    const [first, setFirst] = useState('NetSuite')
    const [second, setSecond] = useState('Shopify')
    const [page, setPage] = useState(0)
    const sectionRef = useRef<HTMLElement>(null)

    // Footer links such as "Salesforce integrations" open this section with that app already picked.
    useEffect(() => {
        if (!router.isReady) return

        const key = typeof router.query.app === 'string' ? router.query.app.toLowerCase() : ''
        const app = showcaseQueryAliases[key]

        if (!app) return

        setFirst(app)
        setSecond(defaultPartner[app] ?? 'Shopify')
        setPage(0)
    }, [router.isReady, router.query.app])

    const workflows = useMemo(() => workflowsFor(first, second), [first, second])
    const pageCount = Math.max(1, Math.ceil(workflows.length / PER_PAGE))
    const visible = workflows.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE)

    const goTo = (next: number) => {
        setPage(next)

        const top = sectionRef.current?.querySelector('[data-grid]')?.getBoundingClientRect().top ?? 0

        if (top < 80) sectionRef.current?.querySelector('[data-grid]')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    const swap = () => {
        setFirst(second)
        setSecond(first)
        setPage(0)
    }

    return (
        <section ref={sectionRef} id='showcase' aria-labelledby='showcase-title' className='border-t border-hairline bg-[#fafafb] py-24 md:py-32'>
            <div className='fc-container'>
                <Reveal className='flex max-w-[640px] flex-col gap-5'>
                    <h2 id='showcase-title' className='text-[clamp(2.25rem,4.6vw,4rem)] font-medium leading-[1.04] tracking-[-0.04em] text-ink'>
                        We&apos;ve built it all
                    </h2>

                    <p className='text-[17px] leading-relaxed text-slate'>
                        FlowCraft has designed and actively monitors more than 2,100 production-grade workflows across 310+ applications. Explore examples of what we have built.
                    </p>
                </Reveal>

                <Reveal delay={100} className='mt-12 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center md:mt-14'>
                    <AppSelect
                        label='First app'
                        value={first}
                        options={showcaseApps}
                        disabledOption={second}
                        onChange={app => {
                            setFirst(app)
                            setPage(0)
                        }}
                    />

                    <button
                        type='button'
                        onClick={swap}
                        aria-label={`Swap ${shortName(first)} and ${shortName(second)}`}
                        className='inline-flex size-11 shrink-0 items-center justify-center self-center rounded-full bg-white text-slate ring-1 ring-inset ring-[#e3e3e9] transition-[color,background-color,rotate] duration-300 hover:rotate-180 hover:bg-mist hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet'
                    >
                        <ArrowLeftRight className='size-[18px]' aria-hidden='true' />
                    </button>

                    <AppSelect
                        label='Second app'
                        value={second}
                        options={showcaseApps}
                        disabledOption={first}
                        onChange={app => {
                            setSecond(app)
                            setPage(0)
                        }}
                    />
                </Reveal>

                <div data-grid className='mt-10 grid scroll-mt-28 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3' aria-live='polite'>
                    {visible.map(workflow => (
                        <article
                            key={`${first}-${second}-${page}-${workflow.description}`}
                            className='fc-fade-up group relative flex min-h-[208px] flex-col gap-4 rounded-[20px] bg-white p-6 shadow-[0_1px_2px_rgba(14,14,14,0.04),0_0_0_1px_rgba(14,14,14,0.06)] transition-[box-shadow,translate] duration-300 hover:-translate-y-1 hover:shadow-[0_22px_40px_-26px_rgba(14,14,14,0.35),0_0_0_1px_rgba(14,14,14,0.08)]'
                        >
                            <span className='flex items-center'>
                                <AppIcon name={workflow.from} size='md' />
                                <AppIcon name={workflow.to} size='md' className='-ml-2 ring-[3px] ring-white' />
                            </span>

                            <div className='flex flex-col gap-1.5'>
                                <h3 className='text-[16px] font-semibold tracking-[-0.015em] text-ink'>
                                    {workflow.title}
                                </h3>

                                <p className='text-[14.5px] leading-relaxed text-slate'>
                                    {workflow.description}
                                </p>
                            </div>

                            <button
                                type='button'
                                onClick={() => openContact({
                                    intent: 'contact',
                                    message: `I'd like FlowCraft to automate work between ${shortName(workflow.from)} and ${shortName(workflow.to)}, starting with this: ${workflow.description.charAt(0).toLowerCase()}${workflow.description.slice(1)}.`
                                })}
                                className='mt-auto inline-flex w-fit items-center gap-1 text-[14px] font-semibold text-violet transition-colors after:absolute after:inset-0 after:rounded-[20px] group-hover:text-violet-bright focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:outline-violet'
                            >
                                Get started
                                <ChevronRight className='size-4 transition-transform duration-300 group-hover:translate-x-0.5' aria-hidden='true' />
                            </button>
                        </article>
                    ))}
                </div>

                <div className='mt-8 flex items-center justify-between gap-4 border-t border-hairline pt-6'>
                    <span className='text-[14px] tabular-nums text-slate'>
                        Page {page + 1} of {pageCount}
                    </span>

                    <div className='flex items-center gap-2'>
                        <button
                            type='button'
                            onClick={() => goTo(page - 1)}
                            disabled={page === 0}
                            className='inline-flex h-10 items-center gap-1 rounded-full px-4 text-[14px] font-medium text-ink ring-1 ring-inset ring-[#e3e3e9] transition-colors hover:bg-white disabled:cursor-not-allowed disabled:text-[#b4b4bc] disabled:ring-[#ececf0] focus-visible:outline-2 focus-visible:outline-violet'
                        >
                            <ChevronLeft className='size-4' aria-hidden='true' />
                            Previous
                        </button>

                        <button
                            type='button'
                            onClick={() => goTo(page + 1)}
                            disabled={page >= pageCount - 1}
                            className='inline-flex h-10 items-center gap-1 rounded-full bg-ink px-4 text-[14px] font-medium text-white transition-colors hover:bg-[#26262b] disabled:cursor-not-allowed disabled:bg-[#e3e3e9] disabled:text-[#9a9aa4] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet'
                        >
                            Next
                            <ChevronRight className='size-4' aria-hidden='true' />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    )
}
